import { IBeneficiarie } from '@smiles/voebiz-core-beneficiaries/dist/Types/BeneficiariesApi.types';
import { IListPurchaseMilesParams } from '@smiles/voebiz-core-corporate-club/dist/Types/GetListPurchaseMiles.types';
import React, { FC, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { getTransactionsList } from 'services/services';
import { capitalize } from 'utils/capitalize';

const exportDefault = {
  exportCsv: () => undefined,
  loading: false,
};

export interface ContextExportCsv {
  exportCsv: () => void;
  loading: boolean;
}

const ExportCsvContext = createContext<Partial<ContextExportCsv>>(exportDefault);

const ExportCsvProvider: FC<{ children: JSX.Element }> = ({ children }) => {
  const { t } = useTranslation(['exportList']);

  const [loading, setLoading] = useState(false);

  const [beneficiaries, setBeneficiaries] = useState<IBeneficiarie[]>([]);

  const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);

  const headers = useMemo(
    () => [
      { label: t('name'), key: 'name' },
      { label: t('cpf'), key: 'cpf' },
      { label: t('email'), key: 'email' },
      { label: t('miles'), key: 'miles' },
      { label: t('created'), key: 'created' },
      { label: t('status'), key: 'status' },
    ],
    [t],
  );

  const formatBeneficiaries = useCallback(
    (items: IBeneficiarie[]) =>
      items.flatMap((item) => {
        return {
          ...item,
          name: capitalize(item.name),
          status: item.status === 'Active' ? t('active') : t('inactive'),
        };
      }),
    [t],
  );

  const getBeneficiaries = useCallback(() => {
    return new Promise<IBeneficiarie[]>((resolve, reject) => {
      try {
        const recursive = async (pageSize: string) => {
          const { data } = await getTransactionsList({
            startRowNum: '0',
            pageSize,
          } as unknown as IListPurchaseMilesParams);
          const response = data as unknown as { beneficiaryList: IBeneficiarie[]; lastPage: boolean };

          if (!response?.lastPage) {
            recursive(pageSize + 99);
          } else {
            const list = formatBeneficiaries(response.beneficiaryList);
            setBeneficiaries(list);
            resolve(response.beneficiaryList);
          }
        };
        recursive('99');
      } catch (error) {
        reject(error);
      }
    });
  }, [formatBeneficiaries]);

  const exportCsv = useCallback(async () => {
    setLoading(true);

    await getBeneficiaries();

    setLoading(false);

    csvLinkRef?.current.link.click();
  }, [getBeneficiaries]);

  const value = useMemo(
    () => ({
      exportCsv,
      loading,
    }),
    [exportCsv, loading],
  );

  return (
    <ExportCsvContext.Provider value={value}>
      <CSVLink
        headers={headers}
        filename="Listagem_De_Beneficiários_Voebiz.txt"
        data={beneficiaries}
        ref={csvLinkRef}
      />
      {children}
    </ExportCsvContext.Provider>
  );
};

const useExportCsv = () => {
  const beneficiaries = useContext(ExportCsvContext);

  if (!beneficiaries) {
    throw new Error('Cannot use `useExportCsv` outside of ExportCsvProvider');
  }
  return beneficiaries;
};

export { ExportCsvContext, ExportCsvProvider, useExportCsv };
